import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { SupportTicketStatic } from "./support-ticket";
import { HttpService } from "../../services/http.service";
import { environment } from "../../../environments/environment";
import { StateService } from "../../services/state.service";
import { MessageService } from "primeng/api";

@Component({
  selector: "app-support-ticket",
  templateUrl: "./support-ticket.component.html",
  styleUrl: "./support-ticket.component.scss",
  standalone: false,
})
export class SupportTicketComponent implements OnInit {
  @Input() visible: boolean = false;
  @Input() header: string = "Dialog Title";
  @Input() content: string = "Dialog content.";
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  supportTktStatic: any = SupportTicketStatic;
  impactOptions = [
    { label: "Highest", value: "highest" },
    { label: "High", value: "high" },
    { label: "Medium", value: "medium" },
    { label: "Low", value: "low" },
  ];
  formData = this.supportTktStatic.FORM_DATA;
  formGroup!: FormGroup;
  impactLevel: { [key: string]: any } = {
    high: false,
    medium: false,
    low: false,
  };
  selectedFiles: File[] = [];
  browserInfo: string = "";
  osInfo: string = "";
  deviceType: string = "";
  fileUploadContent: any = {
    fileList: [],
    fileUploadPercent: 0,
    fileUploadStatus: false,
    fileUploadError: "",
  };
  latestError: any;
  showDialogLoader: boolean = false;
  maxFileCount = 10; // Maximum number of files allowed
  maxFileSize = 10 * 1024 * 1024; // 10MB in bytes
  is_beta_user: boolean = Boolean(
    JSON.parse(localStorage.getItem("user-name")!)?.is_beta_user,
  );
  private readonly userBaseUrl = `${environment.phoBaseUrl.replace(/\/v1\/?$/, "")}/`;

  constructor(
    private httpService: HttpService,
    private stateService: StateService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    // Initialize the form group with controls
    this.initForm();
    this.getDeviceDetails();
  }

  initForm() {
    const formControls: { [key: string]: FormControl } = {};
    this.formData.fields.forEach((field: any) => {
      formControls[field.name] = new FormControl(
        "",
        field.isRequired ? Validators.required : null,
      );
    });
    this.formGroup = new FormGroup(formControls);
  }

  onFormSubmit() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const formValues = this.formGroup.getRawValue();
    this.showDialogLoader = true;
    this.formGroup.disable();
    const formData = new FormData();
    const experimentId =
      formValues.experiment_link?.trim() ||
      sessionStorage.getItem("expId") ||
      "";

    formData.append("experiment_id", experimentId);
    formData.append("title", formValues.title || "");
    formData.append("description", formValues.description || "");
    formData.append("steps_to_reproduce", formValues.steps_to_reproduce || "");
    formData.append("phase_of_detection", this.getPhaseOfDetection());
    formData.append("impact_level", formValues.impact_level || "");

    this.selectedFiles.forEach((file) => {
      formData.append("attachments", file, file.name);
    });

    const url = `${this.userBaseUrl}tickets`;

    this.httpService.postMethod(url, formData).subscribe({
      next: (res) => {
        this.showDialogLoader = false;
        this.closeDialog();
        this.showAlertMessage("info", res.message, res.ticket_id);
      },
      error: (_err) => {
        this.closeDialog();
      },
    });
  }

  private getPhaseOfDetection(): "dev" | "prod" {
    const isQaEnvironment = environment.phoBaseUrl.includes("/qa/");
    return environment.production && !isQaEnvironment ? "prod" : "dev";
  }

  openDialog() {
    this.visible = true;
  }

  closeDialog() {
    this.visible = false;
    this.formGroup.reset();
    this.formGroup.enable();
    this.clearFileSelection();
    this.impactLevel = {
      high: false,
      medium: false,
      low: false,
    };
  }

  onClose() {
    this.closeDialog();
    this.visibleChange.emit(false);
  }

  onFileBrowse(e: any) {
    const fileList: FileList =
      e.target?.files || e.dataTransfer?.files || e.file.dataTransfer.files;

    this.selectedFiles = Array.from(fileList);

    // Check file count
    if (this.selectedFiles.length > this.maxFileCount) {
      this.fileUploadContent.fileUploadError = `You can upload up to ${this.maxFileCount} files`;
      return;
    }

    // Check file sizes
    const oversizedFiles = this.selectedFiles.filter(
      (file) => file.size > this.maxFileSize,
    );
    if (oversizedFiles.length > 0) {
      this.fileUploadContent.fileUploadError = `The maximum allowed file size is ${this.maxFileSize / (1024 * 1024)} MB`;
      return;
    }

    this.selectedFiles.forEach((f: any) => {
      if (this.fileUploadContent.fileList.length < 10) {
        this.fileUploadContent.fileList.push({
          uploadedFile: f,
          fileName: String(f.name),
          filesize: this.stateService.parseFileSize(f.size),
        });
      }
    });
    this.fileUploadContent.fileUploadPercent = 100;
    this.fileUploadContent.fileUploadStatus = true;
  }

  onFileDrop(e: any) {
    this.onFileBrowse(e);
  }

  clearFileSelection() {
    this.fileUploadContent.fileList = [];
    this.selectedFiles = [];
    this.fileUploadContent.fileUploadPercent = 0;
    this.fileUploadContent.fileUploadStatus = false;
    this.fileUploadContent.fileUploadError = "";
  }

  onSelectImpactLevel(type: string) {
    // Reset color selection
    this.impactLevel = {};

    // Toggle color selection
    this.impactLevel[type] = !this.impactLevel[type];
    this.formGroup.get("impact_level")?.setValue(type);
  }

  getDeviceDetails() {
    // Get Browser details
    this.browserInfo = this.getBrowserInfo();

    // Get OS details
    this.osInfo = this.getOSInfo();

    // Determine device type
    this.deviceType = this.isMobile() ? "Mobile" : "Desktop";
  }

  getBrowserInfo(): string {
    const userAgent = navigator.userAgent;

    if (userAgent) {
      return userAgent;
    } else {
      return "Unknown Browser";
    }
  }

  getOSInfo(): string {
    const platform = navigator.platform.toLowerCase();

    if (platform.includes("win")) {
      return "Windows";
    } else if (platform.includes("mac")) {
      return "MacOS";
    } else if (platform.includes("linux")) {
      return "Linux";
    } else if (platform.includes("iphone") || platform.includes("ipad")) {
      return "iOS";
    } else if (platform.includes("android")) {
      return "Android";
    } else {
      return "Unknown OS";
    }
  }

  isMobile(): boolean {
    const userAgent = navigator.userAgent;
    return /Mobi|Android|iPhone|iPad/.test(userAgent);
  }

  showAlertMessage(type: string, heading: string, message: string) {
    this.messageService.clear();
    this.messageService.add({
      key: "s",
      severity: "success",
      summary: heading,
      detail: message,
    });
  }
}
